import React, { useState, useEffect, useCallback } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { PageLoader } from "../components/page-loader";
import "../styles/components/adminpanel.css";
import "../styles/components/OrderHistory.css";

const MONTH_NAMES = [
  "Januar",
  "Februar",
  "Mars",
  "April",
  "Mai",
  "Juni",
  "Juli",
  "August",
  "September",
  "Oktober",
  "November",
  "Desember",
];

const AdminPanel = () => {
  const API_URL = process.env.REACT_APP_API_BASE_URL;
  const { isAuthenticated, user, getAccessTokenSilently } = useAuth0();
  const [accessToken, setAccessToken] = useState(null);
  const [userEmail, setUserEmail] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [orders, setOrders] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [activeTab, setActiveTab] = useState("customers");

  useEffect(() => {
    if (isAuthenticated) {
      getAccessTokenSilently().then((token) => {
        setAccessToken(token);
      });

      if (user && user.email) {
        setUserEmail(user.email);
      }
    }
  }, [isAuthenticated, getAccessTokenSilently, user]);

  const authHeaders = {
    Authorization: `Bearer ${accessToken}`,
    "X-User-Email": userEmail,
  };

  const showError = (message) => {
    setError(message);
    setTimeout(() => setError(null), 6000);
  };

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(null), 4000);
  };

  const formatAmount = (amount) =>
    (amount / 100).toFixed(2).replace(".", ",") + " NOK";

  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString("nb-NO");

  const formatMonth = (dateStr) => {
    const d = new Date(dateStr);
    return `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`;
  };

  const fetchCustomers = useCallback(async () => {
    if (!accessToken || !userEmail) return;
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_URL}/customers`, {
        headers: authHeaders,
      });
      if (!response.ok) throw new Error("Failed to fetch customers");
      const { customers: data } = await response.json();
      setCustomers(data);
    } catch (err) {
      showError(`Error: ${err.message}`);
      console.error("Error:", err);
    } finally {
      setLoading(false);
    }
  }, [accessToken, userEmail, API_URL]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCustomerDetails = async (customer) => {
    setDetailsLoading(true);
    setOrders([]);
    setSubscriptions([]);
    setReports([]);

    try {
      const [ordersRes, subsRes, reportsRes] = await Promise.all([
        fetch(`${API_URL}/customers/${customer.id}/orders`, {
          headers: authHeaders,
        }),
        fetch(`${API_URL}/customers/${customer.id}/subscriptions`, {
          headers: authHeaders,
        }),
        fetch(`${API_URL}/customers/${customer.id}/reports`, {
          headers: authHeaders,
        }),
      ]);

      if (ordersRes.ok) {
        const { orders: data } = await ordersRes.json();
        setOrders(data);
      }
      if (subsRes.ok) {
        const { subscriptions: data } = await subsRes.json();
        setSubscriptions(data);
      }
      if (reportsRes.ok) {
        const { reports: data } = await reportsRes.json();
        setReports(data);
      }
    } catch (err) {
      showError(`Error fetching customer details: ${err.message}`);
    } finally {
      setDetailsLoading(false);
    }
  };

  const viewCustomerDetails = async (customer) => {
    setSelectedCustomer(customer);
    setActiveTab("details");
    await fetchCustomerDetails(customer);
  };

  const updatePartnerStatus = async (customerId, partnerStatus) => {
    try {
      const response = await fetch(
        `${API_URL}/customers/${customerId}/partner`,
        {
          method: "PATCH",
          headers: { ...authHeaders, "Content-Type": "application/json" },
          body: JSON.stringify({ partner: partnerStatus }),
        }
      );

      if (!response.ok) throw new Error("Failed to update partner status");

      setCustomers(
        customers.map((c) =>
          c.id === customerId ? { ...c, partner: partnerStatus } : c
        )
      );
      showSuccess("Partner status updated");
    } catch (err) {
      showError(`Error updating partner status: ${err.message}`);
    }
  };

  const deleteCustomer = async (customerId) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this customer? This cannot be undone."
      )
    )
      return;

    try {
      const response = await fetch(`${API_URL}/customers/${customerId}`, {
        method: "DELETE",
        headers: authHeaders,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to delete customer");
      }

      setCustomers(customers.filter((c) => c.id !== customerId));
      showSuccess("Customer deleted successfully");
    } catch (err) {
      showError(err.message);
    }
  };

  const deleteSubscription = async (subscriptionId) => {
    if (!window.confirm("Are you sure you want to delete this subscription?"))
      return;

    try {
      const response = await fetch(
        `${API_URL}/subscriptions/${subscriptionId}`,
        { method: "DELETE", headers: authHeaders }
      );

      if (!response.ok) throw new Error("Failed to delete subscription");

      setSubscriptions(subscriptions.filter((s) => s.id !== subscriptionId));
      showSuccess("Subscription deleted");
    } catch (err) {
      showError(`Error deleting subscription: ${err.message}`);
    }
  };

  const downloadReport = async (reportId, month) => {
    try {
      const response = await fetch(`${API_URL}/admin/reports/${reportId}`, {
        headers: authHeaders,
      });
      if (!response.ok) throw new Error("Failed to download report");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      a.download = `rapport_${selectedCustomer.email}_${month}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      showError(`Error downloading report: ${err.message}`);
    }
  };

  useEffect(() => {
    if (accessToken && userEmail) fetchCustomers();
  }, [accessToken, userEmail, fetchCustomers]);

  if (loading && activeTab === "customers") return <PageLoader />;

  return (
    <div className="admin-panel">
      <h1>Admin Panel</h1>

      {error && <div className="error-message">{error}</div>}
      {successMessage && (
        <div className="success-message">{successMessage}</div>
      )}

      <div className="admin-tabs">
        <button
          className={activeTab === "customers" ? "active" : ""}
          onClick={() => setActiveTab("customers")}
        >
          Customers
        </button>
        <button
          className={activeTab === "details" ? "active" : ""}
          onClick={() => setActiveTab("details")}
          disabled={!selectedCustomer}
        >
          Customer Details
        </button>
      </div>

      {activeTab === "customers" && (
        <div className="customers-list">
          <table className="customers-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Email</th>
                <th>User ID</th>
                <th>Admin</th>
                <th>Partner</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {customers.map((customer) => (
                <tr key={customer.id}>
                  <td>{customer.id}</td>
                  <td>{customer.email}</td>
                  <td className="user-id-cell">{customer.user_id}</td>
                  <td>{customer.admin ? "Yes" : "No"}</td>
                  <td>
                    <select
                      value={customer.partner ? 1 : 0}
                      onChange={(e) =>
                        updatePartnerStatus(
                          customer.id,
                          parseInt(e.target.value)
                        )
                      }
                    >
                      <option value={0}>No</option>
                      <option value={1}>Yes</option>
                    </select>
                  </td>
                  <td>
                    <button
                      className="view-btn"
                      onClick={() => viewCustomerDetails(customer)}
                    >
                      View Details
                    </button>
                    <button
                      className="delete-btn"
                      onClick={() => deleteCustomer(customer.id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {customers.length === 0 && (
            <div className="no-customers">No customers found</div>
          )}
        </div>
      )}

      {activeTab === "details" && selectedCustomer && (
        <div className="customer-details">
          <button
            className="back-btn"
            onClick={() => setActiveTab("customers")}
          >
            &larr; Back to Customers
          </button>

          <h2>Customer Details: {selectedCustomer.email}</h2>

          {detailsLoading ? (
            <PageLoader />
          ) : (
            <>
              <div className="detail-section">
                <h3>Orders</h3>
                {orders.length > 0 ? (
                  <table className="details-table">
                    <thead>
                      <tr>
                        <th>Order ID</th>
                        <th>Product</th>
                        <th>Amount</th>
                        <th>Date</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map((order) => (
                        <tr key={order.id}>
                          <td>#{order.id}</td>
                          <td>{order.title}</td>
                          <td>{formatAmount(order.amount_paid)}</td>
                          <td>{formatDate(order.created_at)}</td>
                          <td>{order.payment_status}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p>No orders found for this customer.</p>
                )}
              </div>

              <div className="detail-section">
                <h3>Subscriptions</h3>
                {subscriptions.length > 0 ? (
                  <table className="details-table">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Product</th>
                        <th>Package</th>
                        <th>Domain</th>
                        <th>Status</th>
                        <th>Created</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subscriptions.map((sub) => (
                        <tr key={sub.id}>
                          <td>{sub.id}</td>
                          <td>{sub.title}</td>
                          <td>{sub.package_name}</td>
                          <td>{sub.domain || "—"}</td>
                          <td>{sub.status}</td>
                          <td>{formatDate(sub.created_at)}</td>
                          <td>
                            <button
                              className="delete-btn"
                              onClick={() => deleteSubscription(sub.id)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p>No subscriptions found for this customer.</p>
                )}
              </div>

              <div className="detail-section">
                <h3>Monthly Reports</h3>
                {reports.length > 0 ? (
                  <table className="details-table">
                    <thead>
                      <tr>
                        <th>Period</th>
                        <th>Generated</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reports.map((report) => (
                        <tr key={report.id}>
                          <td>{formatMonth(report.month)}</td>
                          <td>{formatDate(report.created_at)}</td>
                          <td>
                            <button
                              className="view-btn"
                              onClick={() =>
                                downloadReport(
                                  report.id,
                                  report.month.slice(0, 7)
                                )
                              }
                            >
                              Download PDF
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p>No monthly reports found for this customer.</p>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
